import { Alert, Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { explainCopy } from '../../lib/explainCopy'
import { CoverageBadge } from './CoverageBadge'
import { StatTile, vizSeqVar } from '../shared/viz'
import type { SimRunResponse } from '../../types/electionSim'

// Histogram SVG geometry (fixed viewBox, rendered responsively via width="100%").
const SVG_W = 320
const SVG_H = 150
const PLOT_LEFT = 10
const PLOT_RIGHT = 312
const PLOT_TOP = 22
const PLOT_BOTTOM = 118

export interface ChamberOutcomeProps {
  run: SimRunResponse
  draws: number
}

const fmtLeverage = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(1)}`

/**
 * Chamber outcome pane (spec §slice-4): majority probability with a delta from the baseline, the
 * median seats won, a seat-count histogram with the majority line, and a coverage callout counted
 * from the seats. The majority-probability tooltip is wired to `explainCopy.majorityProbability`
 * (transparency contract). Lean-only seats are rated from district lean and excluded from the
 * histogram, so the callout says so explicitly.
 */
export function ChamberOutcome({ run, draws }: ChamberOutcomeProps) {
  // Top-of-ticket fallback: chamber-level odds are hidden (backend nulls run.chamber);
  // show a loud estimate banner + the highest-leverage seats from the tornado.
  if (run.coverage.basis === 'topOfTicket') {
    const tornado = run.tornado ?? []
    return (
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
          Chamber control
        </Text>
        <Alert.Root status="warning" borderRadius="md" mb={4} fontSize="sm">
          <Alert.Indicator />
          {explainCopy.topOfTicketBasis(null, 'chamber')}
        </Alert.Root>
        <HStack gap={2} mb={4} wrap="wrap">
          <CoverageBadge coverage="TopOfTicket" />
        </HStack>
        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>
          Highest-leverage seats
        </Text>
        {tornado.length > 0 ? (
          <VStack align="stretch" gap={1}>
            {tornado.map((row) => (
              <HStack key={row.geoUnitId} justify="space-between" fontSize="sm">
                <Text color="gray.700">{row.unitName}</Text>
                <Text fontWeight="semibold" color="gray.600">
                  {fmtLeverage(row.marginalNetDemVotesPer100Shifts)} net votes / 100 shifts
                </Text>
              </HStack>
            ))}
          </VStack>
        ) : (
          <Text fontSize="sm" color="gray.500">
            No seat-level leverage available for this run.
          </Text>
        )}
      </Box>
    );
  }

  // Non-fallback path: backend supplies run.chamber. Guard defensively.
  const chamber = run.chamber
  if (!chamber) return null

  const hist = chamber.seatsHistogram ?? []
  const seats = chamber.seats ?? []
  const minSeats = chamber.histogramMinSeats

  const modeled = seats.filter((s) => s.coverage === 'Modeled').length
  const leanOnly = seats.filter((s) => s.coverage === 'LeanOnly').length
  const insufficient = seats.filter((s) => s.coverage === 'Insufficient').length

  const majorityPct = Math.round(chamber.majorityProbability * 100)
  const basePct = Math.round(chamber.baselineMajorityProbability * 100)
  const improved = majorityPct >= basePct

  const maxVal = hist.reduce((m, v) => Math.max(m, v), 0)
  const barW = hist.length > 0 ? (PLOT_RIGHT - PLOT_LEFT) / hist.length : 0
  const plotH = PLOT_BOTTOM - PLOT_TOP
  // Majority line at the left edge of the first winning seat-count bar.
  const majorityIdx = chamber.majorityThreshold - minSeats
  const majorityX = PLOT_LEFT + majorityIdx * barW
  const majorityInRange = majorityIdx >= 0 && majorityIdx <= hist.length

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
        Chamber control
      </Text>

      <Flex gap={10} align="center" wrap="wrap" mb={4}>
        <StatTile
          label="Probability of holding a majority"
          size="lg"
          value={
            <Tooltip
              showArrow
              content={explainCopy.majorityProbability(draws, majorityPct, modeled, leanOnly)}
              positioning={{
                placement: "top"
              }}
            >
              <Text as="span" cursor="help">
                {majorityPct}%
              </Text>
            </Tooltip>
          }
          delta={{ dir: improved ? 'up' : 'down', value: `from ${basePct}%`, tone: improved ? 'success' : 'danger' }}
        />

        <StatTile label="Median seats won" size="lg" value={chamber.medianSeats} />
      </Flex>

      {hist.length > 0 && (
        <Box>
          <svg
            width="100%"
            viewBox={`0 0 ${SVG_W} ${SVG_H}`}
            role="img"
            aria-label="Distribution of simulated seats won, with the majority line"
          >
            {hist.map((v, i) => {
              const h = maxVal > 0 ? (v / maxVal) * plotH : 0
              const x = PLOT_LEFT + i * barW
              const seatCount = minSeats + i
              const won = seatCount >= chamber.majorityThreshold
              return (
                <g key={i}>
                  <rect
                    x={x + 0.5}
                    y={PLOT_BOTTOM - h}
                    width={Math.max(0.5, barW - 1)}
                    height={h}
                    rx={1}
                    fill={won ? vizSeqVar(400) : 'var(--chakra-colors-border)'}
                  />
                  {seatCount % 5 === 0 && (
                    <text
                      x={x + barW / 2}
                      y={PLOT_BOTTOM + 14}
                      fontSize={9}
                      fill="var(--chakra-colors-muted)"
                      textAnchor="middle"
                    >
                      {seatCount}
                    </text>
                  )}
                </g>
              )
            })}
            {majorityInRange && (
              <>
                <line
                  x1={majorityX}
                  y1={PLOT_TOP - 8}
                  x2={majorityX}
                  y2={PLOT_BOTTOM}
                  stroke="var(--chakra-colors-danger-fg)"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                />
                <text
                  x={majorityX + 4}
                  y={PLOT_TOP - 10}
                  fontSize={9}
                  fill="var(--chakra-colors-danger-fg)"
                  textAnchor={majorityX > SVG_W * 0.7 ? 'end' : 'start'}
                >
                  {chamber.majorityThreshold} = majority
                </text>
              </>
            )}
          </svg>
        </Box>
      )}

      <Box mt={4} bg="gray.50" borderLeftWidth="4px" borderLeftColor="info.fg" borderRadius="md" p={3}>
        <Text fontSize="sm" color="gray.700">
          {modeled} seats Modeled · {leanOnly} Lean-only (rated from district lean, excluded from the
          histogram) · {insufficient} Insufficient data.
        </Text>
      </Box>
    </Box>
  );
}
